import { KeyboardArrowDown, Logout, TuneOutlined } from '@mui/icons-material';
import AccountBoxIcon from '@mui/icons-material/AccountBox';
import GroupsIcon from '@mui/icons-material/Groups';
import {
  Avatar,
  Box,
  ButtonBase,
  Divider,
  Link,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  SvgIcon,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { useMatch } from 'react-router';
import { Link as RouterLink } from 'react-router-dom';
import AttentionRequired from '../../../static/error/AttentionRequired.svg';
import {
  DEBUG_SETTINGS_PATH,
  INSTALLER_TYPES,
  TEAM_SETTINGS_PATH,
  USER_SETTINGS_PATH,
  Z_INDEXES,
} from '../../constants';
import useAuth from '../../hooks/useAuth';
import noticeError from '../../utils/errors/noticeError';
import { Tracker } from '../../utils/tracker';
import { IS_DEV_OR_STAGING } from './userSettings/DebugForm';

const tracker = Tracker.getInstance();

const menuItemStyles = { p: 1.5 };
const listIconStyles = {
  '&.MuiListItemIcon-root': {
    minWidth: 32,
    mr: 0,
    color: 'text.secondary',
  },
};

const AccountPopover = () => {
  const anchorRef = useRef(null);
  const { user, account, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const fullName = `${user.first_name} ${user.last_name}`;
  const missingBankInfo = !(
    account.installer_bank_account_name &&
    account.installer_bank_account_number &&
    account.installer_bank_routing_number &&
    account.installer_bank_name &&
    account.location_of_bank
  );

  const isNotSettingsPage = !useMatch('/settings');
  const isInstallerAccount =
    user.is_installer &&
    (user.installer_role === INSTALLER_TYPES.ADMIN ||
      user.installer_role === INSTALLER_TYPES.ACCOUNTING);
  const isConsultantAccount = user.is_consultant;

  const showBankingInfo =
    isNotSettingsPage &&
    (isInstallerAccount || (isConsultantAccount && account.commission_fee > 0));

  const handleOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleLogout = async () => {
    try {
      handleClose();
      await logout();
      tracker.track(Tracker.EVENTS.LOGOUT);
    } catch (err) {
      noticeError(err);
      toast.error('Unable to logout.');
    }
  };

  const cmq = useMediaQuery('(max-width:1200px)');

  return (
    <>
      <Box
        sx={{
          alignItems: 'center',
          display: 'flex',
          ml: 0,
          position: 'relative',
          zIndex: Z_INDEXES.ACCOUNT_POPOVER,
        }}
      >
        {showBankingInfo && missingBankInfo && (
          <Box
            bgcolor="white"
            p={1.25}
            borderRadius={0.75}
            mb={2.5}
            sx={{
              mr: 'auto',
              height: '70px',
              width: '300px',
              display: 'inline-flex',
              alignItems: 'center',
              position: 'absolute',
              top: '74px',
              right: '40px',
              boxShadow: 'rgba(149, 157, 165, 0.4) 1px 1px 36px 6px;',
            }}
          >
            <SvgIcon component={AttentionRequired} sx={{ fontSize: 42 }} />
            <Typography sx={{ fontSize: '13px', justifyContent: 'left' }}>
              <b>Action Required:</b> Please add your bank information to{' '}
              <Link
                component={RouterLink}
                to={`/${USER_SETTINGS_PATH}`}
                sx={{ textDecoration: 'none' }}
              >
                your account.
              </Link>
            </Typography>
            <Box
              sx={{
                height: '15px',
                width: '25px',
                background: 'white',
                position: 'relative',
                transform: 'rotate(45deg)',
                top: '-35px',
                right: '15%',
                zIndex: 1000,
              }}
            />
          </Box>
        )}
        <Box
          component={ButtonBase}
          data-testid="profileTooltip"
          ref={anchorRef}
          onClick={handleOpen}
          spacing={1}
        >
          <Avatar
            src={user.avatar}
            alt={fullName}
            sx={{
              height: 42,
              width: 42,
            }}
          >
            {fullName[0]}
          </Avatar>
          <Typography
            variant="button"
            noWrap
            sx={{
              ml: 1.5,
              mr: {
                xs: 2,
                sm: 2,
                md: 2,
                lg: 3,
              },
              display: { xl: 'block', xs: cmq ? 'none' : 'block' },
            }}
          >
            Hi, {user.first_name}
          </Typography>
          <Box
            borderRadius="50%px"
            bgcolor={open ? '#F1F7FF' : undefined}
            sx={{ display: { lg: 'block', xs: cmq ? 'none' : 'block' } }}
          >
            <KeyboardArrowDown color="textPrimary" />
          </Box>
        </Box>
      </Box>
      <Menu
        anchorEl={anchorRef.current}
        anchorOrigin={{
          horizontal: 'center',
          vertical: 'bottom',
        }}
        keepMounted
        onClose={handleClose}
        open={open}
        PaperProps={{
          sx: {
            minWidth: '140px',
            filter: 'drop-shadow(0px 7px 20px rgba(40, 47, 56, 0.1))',
            borderRadius: 0.75,
          },
          elevation: 0,
        }}
        MenuListProps={{
          sx: {
            py: 0,
          },
        }}
      >
        <MenuItem
          component={RouterLink}
          to={`/${USER_SETTINGS_PATH}`}
          onClick={handleClose}
          sx={menuItemStyles}
        >
          <ListItemIcon sx={listIconStyles}>
            <AccountBoxIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText
            primary={
              <Typography variant="body1" color="textSecondary">
                My Profile
              </Typography>
            }
          />
        </MenuItem>
        {user?.installer_role === INSTALLER_TYPES.ADMIN ? (
          <MenuItem
            component={RouterLink}
            to={`/${TEAM_SETTINGS_PATH}`}
            onClick={handleClose}
            sx={menuItemStyles}
          >
            <ListItemIcon sx={listIconStyles}>
              <GroupsIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText
              primary={
                <Typography variant="body1" color="textSecondary">
                  Manage Team
                </Typography>
              }
            />
          </MenuItem>
        ) : null}
        {/* Enable feature flags for development and staging environment */}
        {IS_DEV_OR_STAGING && (
          <MenuItem
            component={RouterLink}
            to={`/${DEBUG_SETTINGS_PATH}/`}
            sx={menuItemStyles}
          >
            <ListItemIcon sx={listIconStyles}>
              <TuneOutlined fontSize="small" />
            </ListItemIcon>
            <ListItemText
              primary={
                <Typography variant="body1" color="textSecondary">
                  Debug
                </Typography>
              }
            />
          </MenuItem>
        )}
        <Divider
          flexItem
          variant="middle"
          // Need to use the & selector since base sx class isn't specific enough to override base styles
          sx={{ '&.MuiDivider-middle': { my: 0, mx: 1.5 } }}
        />
        <MenuItem onClick={handleLogout} sx={menuItemStyles}>
          <ListItemIcon sx={listIconStyles}>
            <Logout fontSize="small" />
          </ListItemIcon>
          <ListItemText
            primary={
              <Typography
                color="textSecondary"
                variant="body1"
                textAlign={'start'}
              >
                Log out
              </Typography>
            }
          />
        </MenuItem>
      </Menu>
    </>
  );
};

export default AccountPopover;
